import { useCallback, useMemo } from 'react';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import useV3Budgets from './useV3Budgets';
import { FinancialSummaryMath } from '../../FinancialSummaryMath/FinancialSummaryMath';
import { GlobalCurrency } from '../../GlobalCurrency/GlobalCurrency';
import { buildSiblingGuaranteeExpenses } from '../utils/siblingGuaranteeExpenses';
import { buildEventVenueNet } from '../utils/eventVenueNet';
import { buildPoolEngineOffer, isPoolPreviewActive } from '../utils/buildPoolEngineInput';
import { forOffer } from '../utils/offerScopedRows';

// Shared currency formatter used by both desktop and mobile finance shells.
// Pure helper — exported at module level so the function identity is stable
// across renders and consumers can use it without subscribing to the hook.
export const formatCalculatedNumber = (number, options = {}) => {
  const prefix = options.prefix !== undefined ? options.prefix : "$";
  if (number === null || number === undefined || isNaN(number)) {
    return prefix + "0.00";
  }
  return prefix + Number(number).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// V3BudgetLineItem numeric columns. Rails numericality rejects in-progress
// strings like "2." (a decimal mid-typed before the fractional digits) with
// "is not a number" — even though the column can cast it. The optimistic UI
// keeps the raw string so the user can keep typing, but every save must send a
// real number, so we normalize these fields at the save boundary.
const NUMERIC_LINE_ITEM_FIELDS = [
  'left_hand', 'right_hand',
  'left_hand_forecast', 'right_hand_forecast',
  'left_hand_actual', 'right_hand_actual',
  'min_value', 'max_value',
];

// Coerce numeric line-item fields to real numbers at the save boundary.
// `null`/`undefined` are left untouched (the nil-allowing actuals/min/max fields
// stay nil). Values that resolve to a finite number are sent as that number —
// this covers the in-progress decimal "2." -> 2 and an emptied field "" -> 0.
// Genuinely malformed input ("$", ".", "2.5.3") is dropped from the payload
// rather than coerced to 0, so it is NOT persisted over a real value — the
// server keeps the last saved amount and the field re-syncs on the response.
export const normalizeLineItemNumerics = (params) => {
  const out = { ...params };
  NUMERIC_LINE_ITEM_FIELDS.forEach((field) => {
    if (!(field in out)) return;
    const value = out[field];
    if (value === null || value === undefined) return;
    const num = Number(value);
    if (Number.isFinite(num)) {
      out[field] = num;
    } else {
      delete out[field];
    }
  });
  return out;
};

// Owns the budget-derived state and line-item operations consumed by both
// V3FinancePage (desktop) and V3FinancePageMobile (mobile). Centralises
// plumbing that previously existed byte-for-byte in both shells so the two
// stay in lock-step when either changes.
export default function useV3OfferBudget() {
  const {
    csrfToken,
    team,
    offer,
    budget,
    ticketLineItems,
    additionLineItems,
    incomeLineItems,
    expenseLineItems,
    beforeAdjustedDeductionsLineItems,
    deductionLineItems,
    dealBrackets,
    eventOffers,
    dispatch,
  } = useV3OfferContext();

  const {
    createLineItem,
    updateLineItem,
    deleteLineItem,
    createTicketLineItem,
    createIncomeLineItem,
    createExpenseLineItem,
    createAdditionLineItem,
    createDeductionLineItem,
  } = useV3Budgets();

  const hasSecondaryCurrencySymbol = (
    typeof(team.secondary_currency_symbol) === "string"
      && team.secondary_currency_symbol.length > 0
  );

  const globalCurrency = hasSecondaryCurrencySymbol
    ? new GlobalCurrency(team.currency_conversion_rate, team.secondary_currency_symbol)
    : null;

  const showCurrencyConversion = (
    hasSecondaryCurrencySymbol
      && offer?.display_currency_conversion_for_offer
      && !team.convert_artist_earnings_only
  );

  const budgetForComponents = useMemo(() => {
    if (!budget) return {};
    return {
      ...budget,
      ticket_line_items: ticketLineItems,
      addition_line_items: additionLineItems,
      income_line_items: incomeLineItems,
      expense_line_items: expenseLineItems,
      before_adjusted_deductions_line_items: beforeAdjustedDeductionsLineItems,
      deduction_line_items: deductionLineItems,
      // The V3 deal-brackets editor (O-3125) writes to budget.v3_deal_brackets
      // via ADD/UPDATE/REMOVE_BUDGET_DEAL_BRACKET — the same source the pane
      // reads. The legacy `dealBrackets` slice (SET_DEAL_BRACKETS) is NOT updated
      // by that CRUD, so the engine read the wrong (empty) source and deal
      // brackets never affected the math. Prefer the budget's brackets; fall
      // back to the slice only when the budget carries none.
      v3_deal_brackets: budget.v3_deal_brackets || dealBrackets,
      v3_budget_bonuses: forOffer(budget.v3_budget_bonuses, offer?.id),
      v3_budget_deposits: forOffer(budget.v3_budget_deposits, offer?.id),
    };
  }, [budget, ticketLineItems, additionLineItems, incomeLineItems,
      expenseLineItems, beforeAdjustedDeductionsLineItems, deductionLineItems, dealBrackets, offer?.id]);

  // Synthetic expense rows mirroring V3Budgets::Base#sibling_guarantee_expenses
  // (O-2993). Fed into budgetForMath so FinancialSummaryMath sees sibling
  // guarantees; also surfaced separately for read-only row rendering in
  // ExpensesSection.
  const siblingGuaranteeExpenses = useMemo(
    () => buildSiblingGuaranteeExpenses(offer, eventOffers),
    [offer, eventOffers]
  );

  // Budget the math/totals/breakeven and ProForma should see: sibling rows
  // prepended above manually-entered expenses. Child editor components keep
  // using budgetForComponents (without siblings), since synthetic rows lack
  // the full V3BudgetLineItem shape the row editors expect.
  const budgetForMath = useMemo(() => ({
    ...budgetForComponents,
    expense_line_items: [
      ...siblingGuaranteeExpenses,
      ...(budgetForComponents.expense_line_items || []),
    ],
  }), [budgetForComponents, siblingGuaranteeExpenses]);

  // Live preview. In Independent mode this is byte-for-byte the original
  // single-offer calculation (engine offer === `offer`, budget === budgetForMath
  // with sibling-guarantee expenses). In Pool mode we mirror the server: the
  // pool's shared deal + member list replace the offer's deal (buildPoolEngineOffer),
  // and sibling guarantees are NOT added as expenses (the pool splits the door
  // deal, so we use budgetForComponents without the synthetic sibling rows).
  const calculated = useMemo(() => {
    if (!offer || !budget) return {};
    try {
      const poolActive = isPoolPreviewActive(offer, offer.lineup);
      const engineOffer = poolActive
        ? buildPoolEngineOffer(offer, offer.lineup)
        : offer;
      const engineBudget = poolActive ? budgetForComponents : budgetForMath;
      const calculator = new FinancialSummaryMath(engineOffer, engineBudget, false);
      calculator.solveBreakeven = true;
      const result = JSON.parse(calculator.calculate());
      if (result.breakevenError) {
        console.error('Tickets-to-breakeven solve failed:', result.breakevenError);
      }
      return result;
    } catch (e) {
      console.error('Financial calculation failed:', e);
      return {};
    }
  }, [offer, budgetForMath, budgetForComponents]);

  const budgetForEventNet = useMemo(() => {
    if (!budget) return budgetForComponents;

    return {
      ...budgetForComponents,
      v3_budget_bonuses: budget.v3_budget_bonuses || [],
      v3_budget_deposits: budget.v3_budget_deposits || [],
    };
  }, [budgetForComponents, budget]);

  // Event-level Venue Net Potential — the venue's bottom line across ALL
  // artists on the event, shown identically on every artist tab. Per-artist
  // "Potential Artist Earnings" stays per-tab; only Venue Net rolls up
  // (O-2993 follow-up).
  const eventVenueNet = useMemo(
    () => buildEventVenueNet(eventOffers, budgetForEventNet, offer),
    [eventOffers, budgetForEventNet, offer]
  );

  const handleUpdateBudget = useCallback((updatedBudget) => {
    const allLineItems = [
      ...(updatedBudget.ticket_line_items || []),
      ...(updatedBudget.addition_line_items || []),
      ...(updatedBudget.income_line_items || []),
      ...(updatedBudget.expense_line_items || []),
      ...(updatedBudget.before_adjusted_deductions_line_items || []),
      ...(updatedBudget.deduction_line_items || []),
    ];
    dispatch({ type: V3_ACTIONS.SET_LINE_ITEMS, payload: allLineItems });

    const budgetFields = { ...updatedBudget };
    delete budgetFields.ticket_line_items;
    delete budgetFields.addition_line_items;
    delete budgetFields.income_line_items;
    delete budgetFields.expense_line_items;
    delete budgetFields.before_adjusted_deductions_line_items;
    delete budgetFields.deduction_line_items;
    budgetFields.v3_budget_bonuses = budget?.v3_budget_bonuses || [];
    budgetFields.v3_budget_deposits = budget?.v3_budget_deposits || [];
    dispatch({ type: V3_ACTIONS.SET_BUDGET, payload: budgetFields });
  }, [dispatch, budget]);

  const handleAutoSaveLineItem = useCallback((csrf, t, lineItem) => {
    if (!lineItem?.id) return;
    const { id, created_at, updated_at, ...params } = lineItem;
    updateLineItem(id, normalizeLineItemNumerics(params));
  }, [updateLineItem]);

  const handleDeleteLineItem = useCallback((csrf, t, lineItem) => {
    deleteLineItem(lineItem.id);
  }, [deleteLineItem]);

  // Wrappers bridge child component call signatures (csrfToken, team, budget, position)
  // to V3 hooks which only need (budgetId, position). Wrapped in useCallback so
  // IncomeSection / ExpensesSection and their BudgetLineItemSection children
  // don't get fresh function identities on every render of the parent shell.
  const handleCreateTicketLineItem = useCallback((csrf, t, bgt, position) => {
    createTicketLineItem(bgt.id, position);
  }, [createTicketLineItem]);

  const handleCreateIncomeLineItem = useCallback((csrf, t, bgt, position) => {
    createIncomeLineItem(bgt.id, position);
  }, [createIncomeLineItem]);

  const handleCreateAdditionLineItem = useCallback((csrf, t, bgt, position, performerId = null) => {
    createAdditionLineItem(bgt.id, position, performerId);
  }, [createAdditionLineItem]);

  const handleCreateExpenseLineItem = useCallback((csrf, t, bgt, position) => {
    createExpenseLineItem(bgt.id, position);
  }, [createExpenseLineItem]);

  const handleCreateDeductionLineItem = useCallback((csrf, t, bgt, position) => {
    createDeductionLineItem(bgt.id, position);
  }, [createDeductionLineItem]);

  const handleCreateBeforeAdjustedDeductionsLineItem = useCallback((csrf, t, bgt, position) => {
    createLineItem({
      v3_budget_id: bgt.id,
      category: 'BEFORE_ADJUSTED_DEDUCTIONS',
      name: `Deduction #${position}`,
      operator: '*',
      calc_type: 'per_tickets_sold',
      left_hand: 0,
      right_hand: 0,
    });
  }, [createLineItem]);

  const handleCreateAncillaryIncomeLineItem = useCallback((csrf, t, bgt, position) => {
    createLineItem({
      v3_budget_id: bgt.id,
      category: 'INCOME',
      name: `Ancillary Income #${position}`,
      operator: '*',
      calc_type: 'fixed',
      left_hand: 0,
      // A fixed row totals amount x quantity, so quantity 0 leaves it at $0
      // however large an amount is typed in. The forecast quantity is seeded
      // too because the engine's fallback is asymmetric — the amount treats 0
      // as unset and inherits from Offer, the quantity treats it as set and
      // does not — which is the same pair handleQtyFieldChange writes when a
      // user sets the quantity by hand. (O-4961)
      right_hand: 1,
      right_hand_forecast: 1,
      show_in_offer: false,
    });
  }, [createLineItem]);

  const handleCreateAncillaryExpenseLineItem = useCallback((csrf, t, bgt, position) => {
    createLineItem({
      v3_budget_id: bgt.id,
      category: 'EXPENSES',
      name: `Ancillary Expense #${position}`,
      operator: '*',
      calc_type: 'fixed',
      left_hand: 0,
      // A fixed row totals amount x quantity, so quantity 0 leaves it at $0
      // however large an amount is typed in. The forecast quantity is seeded
      // too because the engine's fallback is asymmetric — the amount treats 0
      // as unset and inherits from Offer, the quantity treats it as set and
      // does not — which is the same pair handleQtyFieldChange writes when a
      // user sets the quantity by hand. (O-4961)
      right_hand: 1,
      right_hand_forecast: 1,
      show_in_offer: false,
    });
  }, [createLineItem]);

  return {
    budgetForComponents,
    siblingGuaranteeExpenses,
    budgetForMath,
    calculated,
    eventVenueNet,
    hasSecondaryCurrencySymbol,
    globalCurrency,
    showCurrencyConversion,
    handleUpdateBudget,
    handleAutoSaveLineItem,
    handleDeleteLineItem,
    handleCreateTicketLineItem,
    handleCreateIncomeLineItem,
    handleCreateAdditionLineItem,
    handleCreateExpenseLineItem,
    handleCreateDeductionLineItem,
    handleCreateBeforeAdjustedDeductionsLineItem,
    handleCreateAncillaryIncomeLineItem,
    handleCreateAncillaryExpenseLineItem,
  };
}
